import { createSlice } from '@reduxjs/toolkit';

import type { InternalChatPendencia } from '../../types';
import { createPendencia, deletePendencia, fetchPendencias, updatePendencia } from './actions';

interface PendenciasByConversa {
  items: InternalChatPendencia[];
  isLoading: boolean;
  error: string | null;
}

interface PendenciasState {
  byConversaId: Record<string, PendenciasByConversa>;
  isMutating: boolean;
  mutateError: string | null;
}

const emptyEntry = (): PendenciasByConversa => ({
  items: [],
  isLoading: false,
  error: null,
});

const initialState: PendenciasState = {
  byConversaId: {},
  isMutating: false,
  mutateError: null,
};

const upsertItem = (entry: PendenciasByConversa, item: InternalChatPendencia): void => {
  const idx = entry.items.findIndex((i) => i.id === item.id);
  if (idx >= 0) entry.items[idx] = item;
  else entry.items.unshift(item);
};

const pendenciasSlice = createSlice({
  name: 'chatAtendimentoPendencias',
  initialState,
  reducers: {
    clearMutateError: (state) => {
      state.mutateError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendencias.pending, (state, action) => {
        const conversaId = action.meta.arg.conversaId;
        const entry = state.byConversaId[conversaId] ?? emptyEntry();
        entry.isLoading = true;
        entry.error = null;
        state.byConversaId[conversaId] = entry;
      })
      .addCase(fetchPendencias.fulfilled, (state, action) => {
        const entry = state.byConversaId[action.payload.conversaId] ?? emptyEntry();
        entry.isLoading = false;
        entry.items = action.payload.items;
        state.byConversaId[action.payload.conversaId] = entry;
      })
      .addCase(fetchPendencias.rejected, (state, action) => {
        const conversaId = action.meta.arg.conversaId;
        const entry = state.byConversaId[conversaId] ?? emptyEntry();
        entry.isLoading = false;
        entry.error = action.payload ?? 'Erro ao carregar pendências';
        state.byConversaId[conversaId] = entry;
      })
      .addCase(createPendencia.pending, (state) => {
        state.isMutating = true;
        state.mutateError = null;
      })
      .addCase(createPendencia.fulfilled, (state, action) => {
        state.isMutating = false;
        const conversaId = action.payload.conversaId;
        const entry = state.byConversaId[conversaId] ?? emptyEntry();
        upsertItem(entry, action.payload);
        state.byConversaId[conversaId] = entry;
      })
      .addCase(createPendencia.rejected, (state, action) => {
        state.isMutating = false;
        state.mutateError = action.payload ?? 'Erro ao criar pendência';
      })
      .addCase(updatePendencia.fulfilled, (state, action) => {
        const conversaId = action.payload.conversaId;
        const entry = state.byConversaId[conversaId] ?? emptyEntry();
        upsertItem(entry, action.payload);
        state.byConversaId[conversaId] = entry;
      })
      .addCase(deletePendencia.fulfilled, (state, action) => {
        const entry = state.byConversaId[action.payload.conversaId];
        if (!entry) return;
        entry.items = entry.items.filter((i) => i.id !== action.payload.pendenciaId);
      });
  },
});

export const { clearMutateError } = pendenciasSlice.actions;
export const pendenciasReducer = pendenciasSlice.reducer;
export type { PendenciasState };
